<template>
  <v-list-item
    :value="path"
    :style="{ paddingLeft: `${depth * 16 + 4}px !important` }"
    class="file-tree-item"
  >
    <template #title>
      <div class="d-flex align-center">
        <span>{{ displayName || name }}</span>
        <v-tooltip
          v-if="readModelMergeInfo"
          location="top"
          max-width="350px"
          text="Read Models are automatically merged into a single API endpoint if they query the same entity with
           the same cardinality (e.g., fetching a single item vs. a list of items)."
        >
          <template #activator="{ props }">
            <span v-bind="props" class="text-caption text-medium-emphasis ml-2">
              ({{ readModelMergeInfo.original }} merged to {{ readModelMergeInfo.merged }})
            </span>
          </template>
        </v-tooltip>
      </div>
    </template>
    <template #prepend>
      <v-icon v-if="item.isFolder" size="16">{{ item.isOpened ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
      <v-icon v-else-if="!isDomainItem" size="16">mdi-file-outline</v-icon>
      <v-icon v-if="isDomainItem" size="16" class="pl-0" :color="itemColor">
        {{ itemIcon }}
      </v-icon>
    </template>
    <template #append>
      <div class="d-flex align-center ga-1">
        <v-icon v-if="isDirty" class="small" color="warning">mdi-circle</v-icon>
        <v-menu v-if="depth < 3" location="bottom" :offset="0">
          <template #activator="{ props }">
            <v-btn
              v-if="!displayName"
              class="show-on-hover-item"
              v-bind="props"
              size="small"
              variant="plain"
              tabindex="-1"
              icon="mdi-dots-vertical"
              :style="{ 'cursor': 'pointer' }"
            />
          </template>
          <template #default>
            <v-list density="compact" class="pt-0">
              <template v-for="item in menuItems" :key="item.label">
                <v-tooltip
                  v-if="item.disabled && item.tooltip"
                  location="right"
                  max-width="400px"
                >
                  <template #activator="{ props }">
                    <div v-bind="props">
                      <v-list-item
                        class="py-0 min-h-0"
                        :disabled="item.disabled"
                        style="pointer-events: none;"
                      >
                        <v-list-item-title class="text-sm d-flex align-center ga-1">
                          <v-icon size="16" class="pa-0">{{ item.icon }}</v-icon>
                          <span>{{ item.label }}</span>
                        </v-list-item-title>
                      </v-list-item>
                    </div>
                  </template>
                  <span>{{ item.tooltip }}</span>
                </v-tooltip>
                <v-list-item
                  v-else
                  class="py-0 min-h-0"
                  @click="item.action"
                  :disabled="item.disabled"
                >
                  <v-list-item-title class="text-sm d-flex align-center ga-1">
                    <v-icon size="16" class="pa-0">{{ item.icon }}</v-icon>
                    <span>{{ item.label }}</span>
                  </v-list-item-title>
                  <v-tooltip
                    v-if="item.tooltip"
                    activator="parent"
                    location="right"
                    max-width="400px"
                  >
                    <span>
                      {{ item.tooltip }}
                    </span>
                  </v-tooltip>
                </v-list-item>
              </template>
            </v-list>
          </template>
        </v-menu>
      </div>
      <!-- <v-tooltip v-if="!isFileEmpty" location="top" open-delay="500">
        <template v-slot:activator="{ props }">
          <v-icon v-if="!!object?.draftContents" class="small" color="warning" v-bind="props">mdi-circle</v-icon>
          <v-icon v-else class="small" color="success" v-bind="props">mdi-check-circle</v-icon>
        </template>
        <span>{{ !!object?.draftContents ? 'Unsaved changes' : 'Up to date' }}</span>
      </v-tooltip> -->
    </template>
  </v-list-item>
</template>

<script setup>
import { computed, inject } from 'vue'
import {
  CODE_EDITOR_APPLY_PATCH,
  CODE_EDITOR_GENERATE_AGAIN,
  CODE_EDITOR_EDIT_PROMPT,
  CODE_EDITOR_COPY_PROMPT
} from '@/codegen/constants'

const {
  item,
  domainObjects,
  canGenerate,
  readModelMergeInfo
} = defineProps({
  item: { type: Object },
  domainObjects: { type: Object },
  canGenerate: { type: Boolean },
  readModelMergeInfo: {
    type: Object,
    default: null
  }
})

const { displayName, name, path, depth } = item

// const props = defineProps({
//   path: { type: String, default: '' },
//   name: { type: String, required: true },
//   displayName: { type: String, required: false },
//   children: { type: Object, required: false },
//   objects: { type: Object, required: false },
//   opened: { type: Array, default: () => [] },
//   dirtyFolders: { type: Set, default: () => new Set() }
// })

// const emit = defineEmits(['domainAction'])

// const isLeafItem = computed(() => {
//   if (!props.children) return true
//   return Object.keys(props.children).length === 0
// })

// const isDirtyFolder = computed(() => props.dirtyFolders.has(props.path + props.name))

const icons = { command: 'mdi-flash', readmodel: 'mdi-magnify', entity: 'mdi-shape-outline', gwt: 'mdi-timeline' }
const colors = { command: 'blue-lighten-1', readmodel: 'green-lighten-1', entity: 'amber-darken-1', gwt: 'purple-lighten-1' }
const itemIcon = computed(() => icons[path.split('/')[1]])
const itemColor = computed(() => colors[path.split('/')[1]])

const isDomainItem = computed(() => depth === 2)

const isDirty = computed(() => {
  const keys = Object.keys(domainObjects)
  const filteredKeys = keys.filter((key) => key.startsWith(path))

  return filteredKeys.some((key) => domainObjects[key].draftContents !== null)
})

const treeAction = inject('treeAction', (data) => {
  console.warn('doDomainAction not provided', data)
})

const menuItems = computed(() => {
  const keys = Object.keys(domainObjects)
  const filteredKeys = keys.filter((key) => key.startsWith(path))

  const res = [
    {
      label: 'Apply Patch',
      icon: 'mdi-file-compare',
      disabled: !isDirty.value,
      action: () => treeAction({ type: CODE_EDITOR_APPLY_PATCH, path, name })
    },
    {
      label: filteredKeys.length === 1 ? 'Generate' : 'Regenerate',
      icon: 'mdi-reload',
      disabled: !canGenerate,
      tooltip: !canGenerate ? 'Framework boilerplate must be selected before generating code' : '',
      action: () => treeAction({ type: CODE_EDITOR_GENERATE_AGAIN, path, name, item })
    },
    {
      label: 'Edit Prompt',
      icon: 'mdi-pencil',
      action: () => treeAction({ type: CODE_EDITOR_EDIT_PROMPT, path, name })
    }
  ]

  if (isDomainItem.value) {
    res.push({
      label: 'Copy Final Prompt',
      icon: 'mdi-content-copy',
      action: () => treeAction({ type: CODE_EDITOR_COPY_PROMPT, path, name })
    })
  }

  return res
})

// const childEntries = computed(() => {
//   const entries = props.children ? Object.entries(props.children) : []
//   entries.sort((a, b) => a[0].localeCompare(b[0]))
//   entries.sort((a, b) => {
//     const aLen = a[1] ? Object.keys(a[1]).length : 0
//     const bLen = b[1] ? Object.keys(b[1]).length : 0
//     return Number(bLen > 0) - Number(aLen > 0)
//   })
//   return entries
// })

// const filepath = computed(() => props.path + props.name)

// const object = computed(() => (props.objects ? props.objects[filepath.value] : undefined))

// const thisOpened = computed(() =>
//   !isLeafItem.value && props.opened.includes(filepath.value)
// )

// const isFileEmpty = computed(() => {
//   const keys = Object.keys(props.objects ?? {})
//   return !keys.includes(props.path + props.name) && props.name !== '__template'
// })

// function domainAction (data) {
//   emit('domainAction', data)
// }
</script>

<style scoped>
v-list {
  --prepend-width: 16px;
}
</style>

<style scoped>
.file-tree-group {
  --prepend-width: 0px !important;
  /* padding-inline-start: var(--indent-padding) !important; */
}

.v-list > .file-tree-group {
  --prepend-width: -16px !important;
}

.v-menu > .v-overlay__content > .v-list {
  padding: 4px !important;
  border-radius: 4px !important;
}

.file-tree-item {
  .show-on-hover-item {
    /* display: none !important; */
    opacity: 0 !important;
  }

  &:hover {
    .show-on-hover-item {
      /* display: block !important; */
      opacity: 1 !important;
    }
  }
}

.v-list-item--density-compact:not(.v-list-item--nav).v-list-item--one-line {
  padding-inline: 4px !important;
}

.v-list-item--density-compact:not(.v-list-item--nav).v-list-item--one-line .file-tree-item {
  padding-inline: 16px !important;
}

.v-list-item--density-compact.v-list-item--one-line {
  min-height: 28px !important;
  padding-top: 0px !important;
  padding-bottom: 0px !important;
}

.file-tree-item {
  padding: 0 4px !important;
  height: 24px !important;
  min-height: 24px !important;
  font-size: 12px !important;
  padding-inline-start: var(--indent-padding) !important;
  /* padding-inline-start: 16px !important; */
  /* --indent-padding: calc(8px + var(--indent-padding)); */
  /* padding-inline-start: calc(2px + var(--indent-padding)) !important; */
}

.file-tree-item .v-btn {
  width: 20px;
  height: 20px;
}

.file-tree-item .v-icon {
  font-size: 20px;
  padding-left: 0px;
}

.file-tree-item .v-icon.small {
  padding-left: 0px;
  font-size: 10px;
}

.file-tree-item .v-list-item__prepend {
  width: 32px;
  min-width: 32px;
  max-width: 32px;
  height: 24px;
}

.file-tree-item .v-list-item__append {
  width: 28px;
  min-width: 28px;
  max-width: 28px;
}
</style>
